// QL-660 — the wording of "who answers on behalf of whom", in one place so the
// web, the PDF blocks and the AI tools never compose the relation themselves.
// Pure and DB-free. The words come from the domain dictionary; sentence frames
// around these phrases stay in each surface's own translation table.

import { answeringWords, representativeRoleLabel } from "./dictionary/answering-roles";
import type { AnsweringPartyRecord, MemberRepresentative } from "./types/answering-party";

/** "{name} ({role})" */
export function representativeWithRole(rep: MemberRepresentative, language: string): string {
  return `${rep.name} (${representativeRoleLabel(rep.role, language)})`;
}

/** "{name} ({role}), on behalf of {memberName}" / "{name} ({rolle}), på vegne av {memberName}" */
export function onBehalfOfText(rep: MemberRepresentative, language: string): string {
  return `${representativeWithRole(rep, language)}, ${answeringWords(language).onBehalfOf} ${rep.memberName}`;
}

/**
 * Fill a surface's own "{name} ({role}), on behalf of {member}" sentence frame with
 * a member's POA/guardian — the one fill the web banners and the PDF notes share.
 */
export function fillRepresentativeTemplate(
  template: string,
  rep: MemberRepresentative,
  language: string,
): string {
  return template
    .replace("{name}", rep.name)
    .replace("{role}", representativeRoleLabel(rep.role, language))
    .replace("{member}", rep.memberName);
}

/**
 * QL-653 — SEVERAL representatives of the same member in one phrase:
 * "A (power of attorney) and B (power of attorney), on behalf of Person 6". One
 * representative gives exactly {@link onBehalfOfText}. All entries must share
 * `memberName`.
 */
export function onBehalfOfListText(
  reps: readonly MemberRepresentative[],
  language: string,
): string {
  const first = reps[0];
  if (!first) return "";
  if (reps.length === 1) return onBehalfOfText(first, language);
  const names = joinRepresentativeNames(
    reps.map((r) => representativeWithRole(r, language)),
    language,
  );
  return `${names}, ${answeringWords(language).onBehalfOf} ${first.memberName}`;
}

/**
 * THE list joiner for names on every surface (web notes, AI text, PDF): "A",
 * "A and B", "A, B and C" (nb: "A, B og C"); empty names are skipped. One locale
 * rule, so a style change is made here only.
 */
export function joinRepresentativeNames(names: readonly string[], language: string): string {
  const locale = language.startsWith("nb") ? "nb" : "en-GB";
  return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(
    names.filter(Boolean),
  );
}

/**
 * One part of a note naming who acts (QL-653): a person by name, or a member slot
 * answered by several representatives of that member's own client, named together
 * "A (power of attorney) and B (power of attorney), on behalf of P".
 */
export type ActingNotePart =
  | { kind: "name"; name: string }
  | { kind: "slot"; representatives: readonly MemberRepresentative[] };

/**
 * The name and head count of a note made of {@link ActingNotePart}s. `count` is the
 * number of PEOPLE (a slot counts each representative), so the singular/plural
 * wording agrees with the answering lines. A slot's phrase has a comma of its own,
 * so when it sits next to other parts they are separated by "; " — "Bob; A (power
 * of attorney) and B (power of attorney), on behalf of P" — never folded into one
 * "and" list that reads as if Bob acted on behalf of P too.
 */
export function actingNote(
  parts: readonly ActingNotePart[],
  language: string,
): { name: string | null; count: number } {
  const phrases: string[] = [];
  let count = 0;
  let hasSlot = false;
  for (const part of parts) {
    if (part.kind === "name") {
      if (!part.name) continue;
      phrases.push(part.name);
      count += 1;
    } else if (part.representatives.length > 0) {
      phrases.push(onBehalfOfListText(part.representatives, language));
      count += part.representatives.length;
      hasSlot = true;
    }
  }
  if (phrases.length === 0) return { name: null, count };
  return {
    name:
      hasSlot && phrases.length > 1
        ? phrases.join("; ")
        : joinRepresentativeNames(phrases, language),
    count,
  };
}

/**
 * QL-653 — true for a line of a member slot that expanded into its
 * representatives: `memberId` is the member while `subjectId` is the
 * representative's `client_party.id`. Views that key a line by one id only (quiz
 * and product-assessment `members[]`, where `memberId` repeats `subjectId`) are
 * recognised by their `memberRepresentative.partyId` being the line's own id.
 */
export function isExpandedRepresentativeLine(line: {
  subjectId: string | null;
  memberId: string | null;
  memberRepresentative?: MemberRepresentative | null;
}): boolean {
  if (line.subjectId === null) return false;
  if (line.memberRepresentative?.partyId === line.subjectId) return true;
  return line.memberId !== null && line.memberId !== line.subjectId;
}

/**
 * The label of one answering line on every live surface (QL-660 D2 + QL-653):
 * - a line of a member slot that expanded into its representatives names its
 *   answerer directly, tense-free: "{rep} ({role}), on behalf of {member}";
 * - a member line with ONE representative is tense-aware: "{member} · answers
 *   through {rep} ({role})" until the slot has an answer, "{member} · answered
 *   by {rep} ({role})" after;
 * - any other line is its own name.
 */
export function answeringLineLabel(
  line: {
    subjectId: string | null;
    memberId: string | null;
    name: string;
    memberRepresentative: MemberRepresentative | null;
  },
  hasAnswer: boolean,
  language: string,
): string {
  const rep = line.memberRepresentative;
  if (!rep) return line.name;
  if (isExpandedRepresentativeLine(line)) return onBehalfOfText(rep, language);
  const t = answeringWords(language);
  return `${line.name} · ${hasAnswer ? t.answeredBy : t.answersThrough} ${representativeWithRole(rep, language)}`;
}

/**
 * QL-660 (D8) — which POA a member line names, and in which tense — the one rule
 * every member line uses (web PA, quiz and declaration lines, AI quiz lines). The
 * line names the answerer RECORDED on the counted answer, never the live POA for
 * an answer they did not give:
 * - QL-653: a member's expanded line is the representative's own → that
 *   representative, tense-free, whatever the result
 * - an advisor override or an external result → just the name (labelled as such)
 * - nothing counts yet                         → "answers through {live POA}"
 * - counted answer by a POA                    → "answered by {that POA}" (the record's;
 *   a record from before QL-660 names the POA by `kind` only, so the live one)
 * - the member's own answer                    → just the name
 */
export function memberLineAttribution(args: {
  /** The line's key; on a member's expanded line (QL-653) it is the representative's own party id. */
  subjectId?: string;
  /** Who answers for the member right now. */
  liveRepresentative: MemberRepresentative | null | undefined;
  /** Whether an answer counts for this line's mode. */
  answered: boolean;
  /** The frozen record of the counted answer, when the caller has it. */
  answeredBy?: Pick<AnsweringPartyRecord, "kind" | "memberRepresentative"> | null;
  /** The counted result is an advisor override or an external attestation. */
  advisorOrExternal?: boolean;
}): { memberRepresentative: MemberRepresentative | null; answered: boolean } {
  const live = args.liveRepresentative ?? null;
  // QL-653 — an expanded line is the representative's own, named "{rep}, on behalf of
  // {member}" whatever the result is (tense-free), an override or external result included.
  if (live && args.subjectId && live.partyId === args.subjectId) {
    return { memberRepresentative: live, answered: args.answered };
  }
  if (args.advisorOrExternal) return { memberRepresentative: null, answered: false };
  if (!args.answered) return { memberRepresentative: live, answered: false };
  if (args.answeredBy?.memberRepresentative) {
    return { memberRepresentative: args.answeredBy.memberRepresentative, answered: true };
  }
  // Records from before QL-660 name the POA by `kind` only; the live one is that person.
  if (args.answeredBy?.kind === "representative") {
    return { memberRepresentative: live, answered: true };
  }
  return { memberRepresentative: null, answered: false };
}
